/**
 * Live authenticated Home projection. Every field is backed by the current
 * catalog, publishing, Translation, Profile, or cover state.
 */
import { z } from "zod";
import { canonicalIdSchema } from "./content.ts";
import {
  emptyWorkSourceProvenance,
  sourceLanguageSchema,
  workSourceProvenanceSchema,
  workStatusSchema
} from "./work.ts";
import { workCoverSchema } from "./workCover.ts";
import { publicDetailHrefSchema } from "./publicUrls.ts";

const labelSchema = z.string().trim().min(1);
const avatarUrlSchema = z.string().trim().min(1).max(1_500_000).nullable();

export const workTicketBadgeSchema = z
  .object({
    kind: z.enum(["format", "genre", "origin", "status"]),
    label: labelSchema
  })
  .strict();

export const emptyWorkCardStats = {
  releaseCount: 0,
  chapterCount: 0,
  sourceUnitCount: 0,
  estimatedReadingMinutes: 0,
  kudosCount: 0
} as const;

export const workCardStatsSchema = z
  .object({
    releaseCount: z.number().int().nonnegative(),
    chapterCount: z.number().int().nonnegative(),
    sourceUnitCount: z.number().int().nonnegative(),
    estimatedReadingMinutes: z.number().int().nonnegative(),
    kudosCount: z.number().int().nonnegative()
  })
  .strict();

export const homeWorkCardSchema = z
  .object({
    id: canonicalIdSchema,
    title: labelSchema,
    summary: labelSchema.nullable(),
    creator: labelSchema,
    creatorAvatarUrl: avatarUrlSchema.default(null),
    fandom: z.object({ id: canonicalIdSchema, name: labelSchema }).strict(),
    ships: z.array(
      z.object({ id: canonicalIdSchema, label: labelSchema }).strict()
    ),
    subscriptionReason: z
      .object({
        kind: z.enum(["ship", "creator"]),
        id: canonicalIdSchema
      })
      .strict()
      .optional(),
    sourceLanguage: sourceLanguageSchema,
    translationState: z.enum(["available", "pending", "failed"]),
    status: workStatusSchema,
    rating: labelSchema.nullable(),
    warnings: z.array(labelSchema),
    tags: z.array(labelSchema),
    ticketBadges: z.array(workTicketBadgeSchema).max(4).default([]),
    stats: workCardStatsSchema.default(emptyWorkCardStats),
    source: workSourceProvenanceSchema.default(emptyWorkSourceProvenance),
    cover: workCoverSchema,
    detailHref: publicDetailHrefSchema.optional()
  })
  .strict();

export const homeResponseSchema = z
  .object({
    displayLanguage: sourceLanguageSchema,
    works: z.array(homeWorkCardSchema).max(60)
  })
  .strict();

export type HomeWorkCard = z.infer<typeof homeWorkCardSchema>;
export type HomeResponse = z.infer<typeof homeResponseSchema>;
